import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { useSessionStore } from '@/deploy/stores/session.store';
import { MOCK_APIS, MOCK_CONTAINERS, MOCK_INSTANCES } from '@/deploy/data/mock';
import styles from './Logs.module.scss';

type SourceKind = 'instance' | 'api';
type Level = 'info' | 'warn' | 'error' | 'debug';

interface Source {
  key: string;   // "instance:<id>" | "api:<id>"
  kind: SourceKind;
  name: string;
  subtitle: string;
  color?: string;
  health: string;
}

interface LogLine {
  t: string;        // ISO
  level: Level;
  message: string;
}

const SAMPLES: Record<SourceKind, string[]> = {
  instance: [
    '[Netty] Player connected: pseudo_{n}',
    'Chunk generation started at {x},{z}',
    'Saved 1384 entities in 42ms',
    'GC Young collection · 110ms · 312MB → 88MB',
    'PalaMod heartbeat OK',
    'SyncMod → mongo ping 18ms',
    'Player pseudo_{n} left: timed out',
    'Faction "WhiteRose" claimed chunk at 1024,1152',
    'Queue matchmaking · 14 players waiting',
    'Warzone round finished in 04:12',
  ],
  api: [
    'GET /api/players?name=pseudo_{n} 200 · 23ms',
    'POST /api/sanctions/ban 201 · 47ms',
    'MongoDB connection OK · 3 pools',
    'Rabbit consumer heartbeat',
    'Ptero API call: server.restart · 200',
    'Cache miss for key profile:{n} · rebuilt in 12ms',
    'Token validated for user {n}',
    'Leaderboard refresh 1/30 slots',
    'Redis latency 2ms',
    'Warning: rate limit touched for ip 10.0.0.{n}',
  ],
};

function randomLine(kind: SourceKind): LogLine {
  const pool = SAMPLES[kind];
  const msg = pool[Math.floor(Math.random() * pool.length)]
    .replace('{n}', String(Math.floor(Math.random() * 9999)))
    .replace('{x}', String(Math.floor(Math.random() * 1000) - 500))
    .replace('{z}', String(Math.floor(Math.random() * 1000) - 500));
  const r = Math.random();
  const level: Level = r > 0.92 ? 'error' : r > 0.8 ? 'warn' : r > 0.4 ? 'info' : 'debug';
  return { t: new Date().toISOString(), level, message: msg };
}

export function Logs() {
  const [searchParams] = useSearchParams();
  const project = useSessionStore((s) => s.currentProject());

  const sources = useMemo<Source[]>(() => {
    const fromInstances = MOCK_INSTANCES
      .filter((i) => i.projectId === project.id)
      .map((i) => {
        const c = MOCK_CONTAINERS.find((c) => c.id === i.containerId);
        return {
          key: `instance:${i.id}`,
          kind: 'instance' as const,
          name: i.name,
          subtitle: `${c?.displayName ?? '?'} · ${i.environment} · ${i.node}`,
          color: c?.color,
          health: i.health,
        };
      });
    const fromApis = MOCK_APIS
      .filter((a) => a.projectId === project.id)
      .map((a) => ({
        key: `api:${a.id}`,
        kind: 'api' as const,
        name: a.name,
        subtitle: `${a.runtime} · ${a.pidOrScreen}`,
        health: a.health,
      }));
    return [...fromApis, ...fromInstances];
  }, [project.id]);

  const fromQuery = useMemo(() => {
    if (searchParams.get('instance')) return `instance:i-${searchParams.get('instance')!.replace(/^i-/, '')}`;
    if (searchParams.get('api')) return `api:${searchParams.get('api')!}`;
    return sources[0]?.key;
  }, [searchParams, sources]);

  const [selectedKey, setSelectedKey] = useState<string>(fromQuery || sources[0]?.key);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState<Level | 'all'>('all');
  const [query, setQuery] = useState('');
  const [lines, setLines] = useState<LogLine[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const selected = sources.find((s) => s.key === selectedKey);

  // Reset quand on change de source
  useEffect(() => {
    const seed: LogLine[] = [];
    for (let i = 0; i < 25; i++) seed.push({ ...randomLine(selected?.kind ?? 'instance'), t: new Date(Date.now() - (25 - i) * 1500).toISOString() });
    setLines(seed);
  }, [selectedKey, selected?.kind]);

  // Stream
  useEffect(() => {
    if (paused || !selected) return;
    const timer = setInterval(() => {
      setLines((prev) => [...prev.slice(-500), randomLine(selected.kind)]);
    }, 900 + Math.random() * 800);
    return () => clearInterval(timer);
  }, [paused, selected]);

  // Autoscroll
  useEffect(() => {
    if (!paused) bottomRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'end' });
  }, [lines, paused]);

  const filtered = lines.filter((l) => {
    if (filter !== 'all' && l.level !== filter) return false;
    if (query && !l.message.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Logs live"
        title="Flux de logs"
        subtitle="Visualiser les logs en temps réel de chaque instance et API · mock côté POC"
      />

      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <Icon name="search" size={14} />
            <input
              type="text"
              placeholder="Filtrer source…"
              onChange={(e) => {
                const q = e.target.value.toLowerCase();
                document.querySelectorAll<HTMLElement>(`[data-source]`).forEach((el) => {
                  el.style.display = el.dataset.source!.toLowerCase().includes(q) ? '' : 'none';
                });
              }}
            />
          </div>
          <div className={styles.sidebarList}>
            {sources.map((s) => (
              <button
                key={s.key}
                data-source={s.name}
                className={[styles.sourceBtn, s.key === selectedKey && styles.sourceActive].filter(Boolean).join(' ')}
                onClick={() => setSelectedKey(s.key)}
              >
                <span
                  className={styles.sourceKind}
                  style={{ background: s.color ?? 'var(--color-accent)' }}
                >
                  <Icon name={s.kind === 'api' ? 'zap' : 'server'} size={11} />
                </span>
                <div className={styles.sourceInfo}>
                  <span className={styles.sourceName}>{s.name}</span>
                  <span className={styles.sourceSub}>{s.subtitle}</span>
                </div>
                <span className={[styles.sourceDot, styles[`health_${s.health}`]].join(' ')} />
              </button>
            ))}
          </div>
        </aside>

        <section className={styles.viewer}>
          <header className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              {selected && (
                <>
                  <h2>{selected.name}</h2>
                  <Badge tone="muted">{selected.subtitle}</Badge>
                </>
              )}
            </div>
            <div className={styles.toolbarRight}>
              <label className={styles.search}>
                <Icon name="search" size={14} />
                <input
                  type="text"
                  placeholder="grep …"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
              <div className={styles.levelFilters}>
                {(['all', 'debug', 'info', 'warn', 'error'] as const).map((lv) => (
                  <button
                    key={lv}
                    className={[styles.lvl, filter === lv && styles.lvlActive].filter(Boolean).join(' ')}
                    onClick={() => setFilter(lv)}
                  >
                    {lv}
                  </button>
                ))}
              </div>
              <button className={styles.pauseBtn} onClick={() => setPaused((v) => !v)}>
                <Icon name={paused ? 'play' : 'clock'} size={12} />
                {paused ? 'Reprendre' : 'Pause'}
              </button>
            </div>
          </header>

          <div className={styles.console}>
            {filtered.length === 0 && <div className={styles.consoleEmpty}>Aucune ligne ne matche…</div>}
            {filtered.map((l, idx) => (
              <div key={idx} className={[styles.line, styles[`line_${l.level}`]].join(' ')}>
                <span className={styles.lineTime}>{new Date(l.t).toLocaleTimeString('fr-FR', { hour12: false })}</span>
                <span className={styles.lineLevel}>{l.level.toUpperCase()}</span>
                <span className={styles.lineMsg}>{l.message}</span>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <footer className={styles.consoleFooter}>
            <span>{filtered.length} ligne{filtered.length > 1 ? 's' : ''} · {lines.length} totales en buffer</span>
            <span>{paused ? '⏸ stream en pause' : '🔴 live'}</span>
          </footer>
        </section>
      </div>
    </div>
  );
}
